/**
 * Generate a v4 UUID for use as the `Idempotency-Key` header on money-moving
 * POSTs. Prefers the native `crypto.randomUUID` (Node 20+, all modern
 * browsers) and falls back to a spec-compliant manual implementation so this
 * module works in non-secure test contexts too.
 *
 * See TECHNICAL_DESIGN.md §3.8 "HTTP client" and
 * .cursor/rules/security.mdc — every route matched by
 * `shouldAttachIdempotencyKey` gets a UUID attached by the HTTP wrapper.
 */

export function newIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // RFC 4122 v4 — cheap fallback. Not cryptographically sampled but enough
  // for client-generated request ids; the BE re-validates uniqueness.
  const bytes = new Uint8Array(16);
  cryptoOrMathRandom(bytes);
  bytes[6] = (bytes[6]! & 0x0f) | 0x40;
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function cryptoOrMathRandom(bytes: Uint8Array): void {
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    crypto.getRandomValues(bytes);
    return;
  }
  for (let i = 0; i < bytes.length; i++) {
    bytes[i] = Math.floor(Math.random() * 256);
  }
}

/**
 * Heuristic for the HTTP wrapper — does this request need an
 * `Idempotency-Key`? Rule of thumb (TDD §3.8): any non-idempotent money or
 * audit-logged action. We err on the side of attaching.
 */
export function shouldAttachIdempotencyKey(method: string, url: string): boolean {
  const m = method.toLowerCase();
  if (m !== 'post' && m !== 'put' && m !== 'patch' && m !== 'delete') return false;

  const path = url.split('?')[0] ?? '';
  return MONEY_MOVING_PATTERNS.some((re) => re.test(path));
}

const MONEY_MOVING_PATTERNS: RegExp[] = [
  /\/refunds(\b|\/)/i,
  /\/adjustments(\b|\/)/i,
  /\/deposits(\b|\/)/i,
  /\/credits(\b|\/)/i,
  /\/orders\/[^/]+\/(cancel-or-return|cancel|refund|void)/i,
  /\/prices(\b|\/)/i,
  /\/approvals(\b|\/)/i,
  /\/cod(\b|\/)/i,
  /\/payouts(\b|\/)/i,
];
